import {
  MasonryComposableCardNode,
  MasonryDriverAction,
} from '@leagueplatform/masonry-renderers';
import type { MasonryEngineMessagingController } from '@leagueplatform/masonry-engine';
import { getTranslation } from 'common/get-translation';
import LINK_CENTRE_SUCCESS_ICON from 'assets/my-health/account-setup-success.png';
import LINK_CENTRE_FAILURE_ICON from 'assets/error-light-icon.svg';

const LINK_CLINIC_RESULT_MODAL_NAMESPACE =
  'medibank-pp-link-clinic-result-modal';

const DEEPLINK_HOME_ACTION = {
  type: 'deeplink' as const,
  namespace: 'default',
  payload: {
    url: '/home',
  },
};

const DISMISS_MODAL_ACTION = {
  type: 'dismissModal' as const,
  namespace: 'default',
  payload: {},
};

const getElementToFocusOnClose = (): HTMLElement =>
  document.activeElement instanceof HTMLElement
    ? document.activeElement
    : document.body;

export type LinkClinicResult = {
  kind: 'success' | 'failure';
  title: string;
  body: string;
  bodySecondary?: string;
  imageSrc: string;
  primary: { label: string; action: MasonryDriverAction };
  secondary?: { label: string; action: MasonryDriverAction };
};

export const linkClinicSuccessResult = (
  clinicName?: string,
): LinkClinicResult => ({
  kind: 'success',
  title: clinicName
    ? getTranslation('STR_LINK_CENTRES_SUCCESS_TITLE', { clinicName })
    : getTranslation('STR_LINK_CENTRES_SUCCESS_TITLE_NO_NAME'),
  body: getTranslation('STR_LINK_CENTRES_SUCCESS_BODY'),
  imageSrc: LINK_CENTRE_SUCCESS_ICON,
  primary: {
    label: getTranslation('STR_LINK_CENTRES_SUCCESS_PRIMARY_BUTTON'),
    action: DEEPLINK_HOME_ACTION,
  },
  secondary: {
    label: getTranslation('STR_LINK_CENTRES_SUCCESS_SECONDARY_BUTTON'),
    action: DISMISS_MODAL_ACTION,
  },
});

export const linkClinicLinkFailedResult = (): LinkClinicResult => ({
  kind: 'failure',
  title: getTranslation('STR_LINK_CENTRES_FAILURE_TITLE'),
  body: getTranslation('STR_LINK_CENTRES_FAILURE_BODY'),
  bodySecondary: getTranslation('STR_LINK_CENTRES_FAILURE_BODY_SECONDARY'),
  imageSrc: LINK_CENTRE_FAILURE_ICON,
  primary: {
    label: getTranslation('STR_LINK_CENTRES_FAILURE_PRIMARY_BUTTON'),
    action: DISMISS_MODAL_ACTION,
  },
});

export const linkClinicGenericErrorResult = (): LinkClinicResult => ({
  kind: 'failure',
  title: getTranslation('STR_ERROR_STATE_HEADING_GENERIC'),
  body: getTranslation('STR_ERROR_STATE_BODY_API'),
  imageSrc: LINK_CENTRE_FAILURE_ICON,
  primary: {
    label: getTranslation('STR_ERROR_STATE_CTA_DISMISS'),
    action: DISMISS_MODAL_ACTION,
  },
});

type LinkClinicResultModalBody = NonNullable<
  NonNullable<MasonryComposableCardNode['properties']>['body']
> & {
  subheadingSecondary?: string;
};

export const showLinkClinicResultModal = (
  messagingController: MasonryEngineMessagingController,
  result: LinkClinicResult,
): void => {
  const body: LinkClinicResultModalBody = {
    heading: result.title,
    subheading: result.body,
    imageSrc: result.imageSrc,
    imageAltText: '',
    ...(result.bodySecondary
      ? { subheadingSecondary: result.bodySecondary }
      : {}),
  };

  const node: MasonryComposableCardNode = {
    id: `link-clinic-result-${result.kind}`,
    type: 'composableCard',
    namespace: LINK_CLINIC_RESULT_MODAL_NAMESPACE,
    properties: {
      body,
      cta: {
        firstCTA: { label: result.primary.label },
        ...(result.secondary
          ? {
              secondCTA: {
                label: result.secondary.label,
                type: 'secondary',
              },
            }
          : {}),
      },
    },
    actions: {
      onFirstButtonClick: result.primary.action,
      ...(result.secondary
        ? { onSecondButtonClick: result.secondary.action }
        : {}),
    },
    sections: {},
  };

  messagingController.sendMessageToInstance({
    type: 'showModal',
    payload: {
      title: result.title,
      hideTitle: true,
      showCloseButton: false,
      content: { kind: 'node', node },
      elementToFocusOnClose: getElementToFocusOnClose(),
    },
  });
};
